import { Copy } from 'lucide-react'
import { toast } from 'sonner'
import { WidgetInstallSection } from '@/components/chat-builder/widget-install/WidgetInstallSection'
import { FEATURE_FLAGS } from '@/constants/featureFlags'
import { useConfigurations } from '@/hooks/useConfigurations'
import { useOrganizationFeatureFlag } from '@/hooks/useOrganizationFeatureFlag'
import { useAuthStore } from '@/stores/auth'
import type { ChatConfiguration } from '@/stores/chatBuilderStore'
import {
  generateDeploymentCodes,
  getDeploymentCodeString,
  vwamScriptUrl,
} from '@/utils/deploymentCodes'

// Helper function to copy text to clipboard
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  } catch {
    toast.error('Failed to copy to clipboard')
  }
}

export function DeploymentSection() {
  // Get published configurations for deployment
  const { published: publishedConfigurations } = useConfigurations({
    status: 'published',
  })
  const { organization } = useAuthStore()
  const { enabled: pageViewCaptureFlag } = useOrganizationFeatureFlag(
    FEATURE_FLAGS.PAGE_VIEW_CAPTURE
  )
  // Loader page-view capture reads this; omitted when absent so the install still works.
  const orgAttribute =
    pageViewCaptureFlag && organization?.id
      ? ` data-vwam-org="${organization.id}"`
      : ''
  const globalScript = `<script src="${vwamScriptUrl}"${orgAttribute} async></script>`
  const widgetConfigurations = publishedConfigurations.filter(
    (config) =>
      config.deploymentType === 'widget' ||
      config.enabledDeploymentTypes?.includes('widget')
  )

  return (
    <div className="space-y-6">
      {/* Global Script Section */}
      <div className="space-y-1.5">
        <h3 className="text-base font-semibold text-card-foreground">
          Global Script
        </h3>
        <p className="text-sm text-muted-foreground">
          Copy the following script to automatically populate and update your
          agents.
        </p>
      </div>

      {widgetConfigurations.length > 0 && (
        <WidgetInstallSection
          title="Sitewide widgets"
          entries={widgetConfigurations.map((config) => ({
            id: config.id,
            name: config.name,
            script: generateDeploymentCodes(config, 'widget').script,
          }))}
          copyLabel="Copy Widget Script"
          onCopy={copyToClipboard}
        />
      )}

      <div className="border border-primary rounded-2xl overflow-hidden">
        <div className="bg-black/90 p-5">
          <div className="font-mono text-sm text-white/40">
            <span className="text-blue-400/60">&lt;script</span>{' '}
            <span className="text-blue-400">src</span>=
            <span className="text-orange-300">"{vwamScriptUrl}"</span>
            {orgAttribute && (
              <>
                {' '}
                <span className="text-blue-400">data-vwam-org</span>=
                <span className="text-orange-300">"{organization?.id}"</span>
              </>
            )}{' '}
            <span className="text-blue-400">async</span>
            <span className="text-blue-400/60">&gt;&lt;/script&gt;</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => copyToClipboard(globalScript)}
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
        >
          <Copy className="h-4 w-4" />
          Copy Script
        </button>
      </div>

      {/* Agent Containers Section */}
      <div className="space-y-1.5">
        <h3 className="text-base font-semibold text-card-foreground">
          Agent Containers
        </h3>
        <p className="text-sm text-muted-foreground">
          Copy the following container code where you would like the chat to
          populate on site.
        </p>
      </div>

      <div className="border border-border rounded-2xl overflow-hidden bg-card">
        <div className="overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[200px_1fr_auto] bg-secondary/50">
            <div className="px-6 py-3 border-b border-border">
              <div className="text-sm font-medium text-foreground">Agent</div>
            </div>
            <div className="px-6 py-3 border-b border-border">
              <div className="text-sm font-medium text-foreground">
                Deployment Code
              </div>
            </div>
            <div className="px-6 py-3 border-b border-border">
              <div className="text-sm font-medium text-foreground">&nbsp;</div>
            </div>
          </div>

          {/* Table Rows */}
          {publishedConfigurations.length > 0 ? (
            publishedConfigurations.map((config: ChatConfiguration) => {
              const deploymentCode = getDeploymentCodeString(
                config,
                organization?.id
              )

              return (
                <div
                  key={config.id}
                  className="grid grid-cols-[200px_1fr_auto]"
                >
                  <div className="px-6 py-3 border-b border-border last:border-b-0">
                    <div className="text-sm font-medium text-foreground overflow-hidden text-ellipsis">
                      {config.name}
                    </div>
                  </div>
                  <div className="px-6 py-3 border-b border-border last:border-b-0">
                    <div className="text-sm font-medium text-foreground overflow-hidden text-ellipsis font-mono">
                      {deploymentCode}
                    </div>
                  </div>
                  <div className="px-[18px] py-3 border-b border-border last:border-b-0 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => copyToClipboard(deploymentCode)}
                      className="p-2 hover:bg-secondary rounded-full transition-colors"
                    >
                      <Copy className="h-4 w-4 text-foreground" />
                    </button>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="grid grid-cols-[200px_1fr_auto]">
              <div className="px-6 py-12 col-span-3 text-center">
                <p className="text-sm text-muted-foreground">
                  No published agents found. Publish an agent to see deployment
                  code.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
